import * as React from "react";
import { homeLink, request } from "../../../../../services/cpln";
import { Button, Card, Space, Tooltip } from "antd";
import { CardTitle } from "../../../../../components/antd/card/CardTitle";
import { CardContent } from "../../../../../components/antd/card/CardContent";
import { Mk8sInput } from "../../../../../schema/types/mk8s/input/mk8s";
import { CardCollapsibleContent } from "../../../../../components/antd/card/CardCollapsibleContent";
import { CaretRightOutlined, EllipsisOutlined, PlusOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import { NGProviderBrowser } from "../../../ngProviderBrowser";
import { inputToMk8s } from "../../../draft/utils/inputToMk8s";
import { ProviderPreInstallScript } from "../../../advanced/providerPreInstallScript";
import { ProviderAutoscaler } from "../../../advanced/providerAutoscaler";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { CpInput } from "../../../../gvc/testNewForm/CpInput";
import { CpFormItem } from "../../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../../../../gvc/testNewForm/CpErrorWarning";
import { CpArrayString } from "../../../../gvc/testNewForm/CpArrayString";
import { store } from "../../../../../store";
import { cloneDeep } from "lodash";
import { useCpFormContext } from "../../../../../components/form/useCpFormContext";
import { CpErrorWarningArrayIcon } from "../../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { CpKindSelect } from "../../../../gvc/testNewForm/temp/CpKindSelect";
import { EmbeddedSecretCreate } from "../../../../secret/create/embedded";
import { useDrawerContext } from "../../../../../components/antd/drawerContext";
import { parseLink } from "../../../../../utils/linkParser/linkParser";
import { CpLabelTooltip } from "../../../../gvc/testNewForm/CpLabelTooltip";
import { CpTooltip } from "../../../../gvc/testNewForm/CpTooltip";

interface Props {}
export const LambdalabsProviderSelf: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();

  const drawer = useDrawerContext();

  const [browserKey, setBrowserKey] = React.useState("");
  const [isAdvancedCollapsed, setIsAdvancedCollapsed] = React.useState(true);

  const region = useCpFormValue(`lambdalabs.region`);
  const sshKey = useCpFormValue(`lambdalabs.sshKey`);
  const fileSystems = useCpFormValue(`lambdalabs.fileSystems`);

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>
            Lambdalabs Provider
            <CpErrorWarningArrayIcon
              names={[
                `lambdalabs.region`,
                `lambdalabs.tokenSecretLink`,
                `lambdalabs.sshKey`,
                `lambdalabs.preInstallScript`,
                `lambdalabs.autoscaler`,
                `lambdalabs.fileSystems`,
              ]}
            />
          </CardTitle>
          <CardContent>
            <CpFormItem>
              <CpLabel name={`lambdalabs.region`}>Region</CpLabel>
              <Space.Compact>
                <CpInput name={`lambdalabs.region`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("region")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`lambdalabs.region`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabelTooltip>
                <CpLabel name={`lambdalabs.tokenSecretLink`}>Lambdalabs Token Secret</CpLabel>
                <CpTooltip
                  tooltipProps={{
                    title: `An opaque secret that contains your Lambda Labs token, it will be used to manage servers in your Lambda Labs account`,
                  }}
                />
              </CpLabelTooltip>
              <Space.Compact>
                <CpKindSelect
                  kind="secret"
                  name={`lambdalabs.tokenSecretLink`}
                  optionValue="name"
                  queries={[{ property: "type", value: "opaque" }]}
                  style={{ width: 600 }}
                />
                <Tooltip title={`Create an Opaque Secret`}>
                  <Button
                    onClick={() => {
                      drawer.setPersistent(
                        `mk8s-lambdalabs-create-secret`,
                        "Create Opaque Secret",
                        <>
                          <EmbeddedSecretCreate
                            onCancel={() => drawer.closePersistent()}
                            onDone={(itemLink) => {
                              drawer.closePersistent();
                              const { name } = parseLink(itemLink, { useInputCtx: true });
                              const secretState = {
                                value: name,
                                name: name,
                                link: itemLink,
                              };
                              formSetValue(id, `lambdalabs.tokenSecretLink`, secretState);
                              formTriggerValidation(id);
                            }}
                            lockedType={`opaque`}
                          />
                        </>,
                        600,
                      );
                    }}
                    icon={<PlusOutlined />}
                  />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`lambdalabs.tokenSecretLink`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`lambdalabs.sshKey`}>SSH Key</CpLabel>
              <Space.Compact>
                <CpInput name={`lambdalabs.sshKey`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("ssh-keys")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`lambdalabs.sshKey`} />
            </CpFormItem>
            <div style={{ maxWidth: 600 }}>
              <CpArrayString label={`File Systems`} name={`lambdalabs.fileSystems`} />
            </div>
            <div className="mt-4">
              <CardTitle style={{ marginBottom: 0, fontSize: 20 }}>
                <div className={`flex items-baseline ${isAdvancedCollapsed ? "" : "mb-4"}`}>
                  <div
                    className={`flex items-baseline gap-2 color-link-hover cursor-pointer`}
                    onClick={() => {
                      setIsAdvancedCollapsed((x) => !x);
                    }}
                  >
                    <CaretRightOutlined rotate={isAdvancedCollapsed ? 0 : 90} className={`self-center`} />
                    <span>Advanced</span>
                  </div>
                </div>
              </CardTitle>
            </div>
            <CardCollapsibleContent collapsed={isAdvancedCollapsed}>
              <ProviderPreInstallScript provider="lambdalabs" />
              <ProviderAutoscaler provider="lambdalabs" />
            </CardCollapsibleContent>
          </CardContent>
        </Card>
      </div>
      {browserKey === "region" ? (
        <NGProviderBrowser
          title="Browse Lambda Labs Regions"
          initialValue={region}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "regions",
                cluster: inputToMk8s(values),
              },
            });
            return data.data;
          }}
          onOk={(value) => {
            formSetValue(id, `lambdalabs.region`, value as string);
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Description"]}
          getData={(item: any) => [item.name, item.name, item.description]}
        />
      ) : null}
      {browserKey === "ssh-keys" ? (
        // TODOMK8S fix
        <NGProviderBrowser
          title="Browse Lambda Labs SSH Keys"
          initialValue={sshKey}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "ssh-keys",
                cluster: inputToMk8s(values),
              },
            });
            return data.data;
          }}
          onOk={(value) => {
            formSetValue(id, `lambdalabs.sshKey`, value as string);
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name"]}
          getData={(item: any) => [String(item.name), item.name]}
        />
      ) : null}
      {browserKey === "file-systems" ? (
        <NGProviderBrowser
          title="Browse Lambda Labs File Systems"
          initialValue={fileSystems.map((i) => i.value)}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "file-systems",
                cluster: inputToMk8s(values),
              },
            });
            return data.data;
          }}
          multipleChoice
          onOk={(_value) => {
            const values: string[] = _value as any;
            const form = store.getState().form.instances[id];
            const formValues: Mk8sInput = form.values as any;
            const _fileSystems = cloneDeep(formValues.lambdalabs.fileSystems);

            for (const value of values) {
              if (_fileSystems.some((i) => i.value === value)) {
                continue;
              }
              _fileSystems.push({ id: uuidv4(), value: value });
            }
            formSetValue(id, `lambdalabs.fileSystems`, _fileSystems);
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Region", "Status", "Created By (Email)"]}
          getData={(item: any) => [
            String(item.name),
            item.name,
            item.region?.name || "",
            item.created_by?.status || "",
            item.created_by?.email || "",
          ]}
        />
      ) : null}
    </>
  );
};
